import { FieldConfig } from "@/types/fieldConfig";
import { Organization } from "@/types/organization";
import { User } from "@/types/user";
import {
    Button,
    Drawer,
    DrawerBody,
    DrawerContent,
    DrawerFooter,
    DrawerHeader,
    Chip,
} from "@heroui/react";
import { ReactNode } from "react";
import {
    ManagementZoneChip,
    NPlanButton,
    PlotStatusCheckpoints,
    PlotStatusChip,
    SoilManagementButton,
    isSoilIncomplete,
    getPlantingSource,
    ConnectionsCell,
} from "@/components/home/FieldConfigCells";
import { Link } from "@tanstack/react-router";
import dayjs from "dayjs";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUpRightFromSquare } from "@fortawesome/free-solid-svg-icons";

type MobileFieldConfigDetailDrawerProps = {
    field: FieldConfig | null;
    isOpen: boolean;
    onClose: () => void;
    growersById: Record<number, Organization>;
    serviceProvidersById: Record<number, Organization>;
    accountManagersById: Record<number, User>;
    isConsultantOrSentinel: boolean;
    userIsSentinel: boolean;
    onEditNPlan: (field: FieldConfig) => void;
    onEditSoil: (field: FieldConfig) => void;
};

function DetailRowItem({ label, value }: { label: string; value: ReactNode }) {
    return (
        <div className="flex flex-row justify-between items-start gap-4 py-2 border-b border-gray-100 last:border-0">
            <div className="text-sm text-gray-600 shrink-0">{label}</div>
            <div className="text-sm text-right font-medium">
                {value ?? "--"}
            </div>
        </div>
    );
}

export default function MobileFieldConfigDetailDrawer({
    field,
    isOpen,
    onClose,
    growersById,
    serviceProvidersById,
    accountManagersById,
    isConsultantOrSentinel,
    userIsSentinel,
    onEditNPlan,
    onEditSoil,
}: MobileFieldConfigDetailDrawerProps) {
    if (!field) return null;

    const growerName = growersById[field.grower]?.name;
    const serviceProviderName =
        serviceProvidersById[field.service_provider]?.name;
    const accountManagerName =
        accountManagersById[field.account_manager]?.full_name;

    return (
        <Drawer
            isOpen={isOpen}
            onClose={onClose}
            placement="bottom"
            size="lg"
            scrollBehavior="inside"
            classNames={{
                base: "h-[80vh] max-h-[80vh]",
            }}
        >
            <DrawerContent>
                <DrawerHeader>
                    <div className="flex flex-col items-start">
                        <Link
                            to="/dashboard/fields/$fieldId/insights/ntime"
                            params={{
                                fieldId: field.id.toString(),
                            }}
                            search={{
                                advisor: undefined,
                            }}
                            target="_blank"
                            className="font-semibold text-lg text-primary hover:underline"
                        >
                            {field.name}
                        </Link>

                        {growerName && (
                            <div className="text-xs text-gray-600">
                                {growerName}
                            </div>
                        )}
                    </div>
                </DrawerHeader>

                <DrawerBody>
                    <div className="flex flex-col">
                        {isConsultantOrSentinel && (
                            <DetailRowItem
                                label="Grower"
                                value={growerName ?? "--"}
                            />
                        )}
                        {userIsSentinel && (
                            <>
                                <DetailRowItem
                                    label="CSP"
                                    value={serviceProviderName ?? "--"}
                                />
                                <DetailRowItem
                                    label="Account Manager"
                                    value={accountManagerName ?? "--"}
                                />
                            </>
                        )}
                        <DetailRowItem
                            label="Active Crop"
                            value={
                                field.active_planting_operation
                                    ?.crop_configuration?.crop_name ?? "--"
                            }
                        />
                        <DetailRowItem
                            label="Planting Date"
                            value={
                                field.active_planting_operation?.start_date
                                    ? dayjs(
                                        field.active_planting_operation
                                            .start_date,
                                    ).format("ll")
                                    : "--"
                            }
                        />
                        <DetailRowItem
                            label="Crop Source"
                            value={getPlantingSource(field) ?? "--"}
                        />
                        <div className="flex flex-row justify-between items-center gap-4 py-2 border-b border-gray-100">
                            <div className="text-sm text-gray-600 shrink-0">
                                Plot Status
                            </div>
                            <div className="flex flex-col items-end gap-1">
                                <PlotStatusCheckpoints field={field} />
                                <PlotStatusChip field={field} />
                            </div>
                        </div>
                        <DetailRowItem
                            label="Irrigation"
                            value={
                                <Chip
                                    as={Link}
                                    to="/dashboard/fields/$fieldId/setup/irrigation-system"
                                    params={{
                                        // @ts-expect-error ts(2322)
                                        fieldId: field.id.toString(),
                                    }}
                                    target="_blank"
                                    size="sm"
                                    radius="sm"
                                    variant="flat"
                                    color="primary"
                                    className="cursor-pointer"
                                >
                                    Irrigation System
                                </Chip>
                            }
                        />
                        <DetailRowItem
                            label="Management Zones"
                            value={<ManagementZoneChip field={field} />}
                        />
                        <DetailRowItem
                            label="Connections"
                            value={<ConnectionsCell field={field} />}
                        />
                        {userIsSentinel && (
                            <DetailRowItem
                                label="N Plan"
                                value={
                                    <NPlanButton
                                        field={field}
                                        onPress={() => onEditNPlan(field)}
                                    />
                                }
                            />
                        )}
                        <DetailRowItem
                            label="Soil & Management"
                            value={
                                <SoilManagementButton
                                    incomplete={
                                        field.soil_management_required &&
                                        isSoilIncomplete(
                                            field,
                                            field.current_crop_cycle,
                                        )
                                    }
                                    onPress={() => onEditSoil(field)}
                                />
                            }
                        />
                    </div>
                </DrawerBody>

                <DrawerFooter className="flex flex-col gap-2">
                    <Link
                        to="/dashboard/fields/$fieldId/insights"
                        params={{
                            fieldId: field.id.toString(),
                        }}
                        search={{
                            advisor: undefined,
                        }}
                        onClick={onClose}
                        className="w-full"
                    >
                        <Button
                            as="span"
                            fullWidth
                            color="primary"
                            variant="flat"
                            startContent={
                                <FontAwesomeIcon icon={faUpRightFromSquare} />
                            }
                        >
                            Open Field
                        </Button>
                    </Link>

                    <Button fullWidth color="primary" onPress={onClose}>
                        Close
                    </Button>
                </DrawerFooter>
            </DrawerContent>
        </Drawer>
    );
}
